/* Lunemi — interactions for custom sections (no dependencies) */
(() => {
  if (window.__lunemiLoaded) return;
  window.__lunemiLoaded = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Hero slideshow ---------- */
  class LxHero extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.lx-hero__slide')];
      this.dots = [...this.querySelectorAll('.lx-hero__dot')];
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.delay = (parseInt(this.dataset.autoplay, 10) || 0) * 1000;
      if (this.slides.length < 2) return;

      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i, true)));

      let x0 = null;
      this.addEventListener('touchstart', (e) => (x0 = e.touches[0].clientX), { passive: true });
      this.addEventListener('touchend', (e) => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0;
        if (Math.abs(dx) > 50) this.go(this.index + (dx < 0 ? 1 : -1), true);
        x0 = null;
      });
      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.indexOf(e.target);
        if (i > -1) { this.go(i); this.pause(); }
      });
      this.play();
    }
    disconnectedCallback() { clearTimeout(this.timer); }
    go(i, user) {
      const n = this.slides.length;
      this.index = (i + n) % n;
      this.slides.forEach((s, k) => {
        const on = k === this.index;
        s.classList.toggle('is-active', on);
        s.setAttribute('aria-hidden', String(!on));
        s.querySelectorAll('a, button').forEach((el) => (on ? el.removeAttribute('tabindex') : el.setAttribute('tabindex', '-1')));
      });
      this.dots.forEach((d, k) => {
        d.classList.toggle('is-active', k === this.index);
        d.setAttribute('aria-current', String(k === this.index));
        // restart progress animation
        if (k === this.index) { d.classList.remove('is-active'); void d.offsetWidth; d.classList.add('is-active'); }
      });
      if (user) this.play();
    }
    play() {
      clearTimeout(this.timer);
      if (!this.delay || reduceMotion || window.Shopify?.designMode) { this.setAttribute('data-paused', ''); return; }
      this.removeAttribute('data-paused');
      this.timer = setTimeout(() => this.go(this.index + 1), this.delay);
    }
    pause() { clearTimeout(this.timer); this.setAttribute('data-paused', ''); }
  }
  customElements.get('lx-hero') || customElements.define('lx-hero', LxHero);

  /* ---------- Horizontal rail with arrows + progress ---------- */
  class LxRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.lx-rail');
      if (!this.track) return;
      this.prev = this.querySelector('[data-prev]');
      this.next = this.querySelector('[data-next]');
      this.bar = this.querySelector('.lx-progress span');
      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', () => this.update(), { passive: true });
      new ResizeObserver(() => this.update()).observe(this.track);
      this.update();
    }
    step(dir) {
      const item = this.track.children[0];
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const w = item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
      const perView = Math.max(1, Math.round(this.track.clientWidth / w));
      this.track.scrollBy({ left: dir * w * perView, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const max = scrollWidth - clientWidth;
      if (this.prev) this.prev.disabled = scrollLeft <= 2;
      if (this.next) this.next.disabled = scrollLeft >= max - 2;
      if (this.bar) {
        const ratio = scrollWidth ? clientWidth / scrollWidth : 1;
        this.bar.style.width = `${Math.min(100, ratio * 100)}%`;
        const pos = max > 0 ? scrollLeft / max : 0;
        this.bar.style.transform = `translateX(${pos * (1 / Math.max(ratio, 0.0001) - 1) * 100}%)`;
        this.bar.parentElement.hidden = ratio >= 0.999;
      }
    }
  }
  customElements.get('lx-rail') || customElements.define('lx-rail', LxRail);

  /* ---------- Tabs ---------- */
  class LxTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(n);
            this.tabs[n].focus();
          }
        });
      });
      this.addEventListener('shopify:block:select', (e) => {
        const i = this.tabs.findIndex((t) => t.dataset.blockId === e.target.dataset.blockId);
        if (i > -1) this.select(i);
      });
    }
    select(i) {
      this.tabs.forEach((tab, k) => {
        const on = k === i;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
        const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
        if (!panel) return;
        panel.hidden = !on;
        if (on) {
          panel.classList.remove('is-entering');
          void panel.offsetWidth;
          panel.classList.add('is-entering');
          panel.querySelector('lx-rail')?.update?.();
        }
      });
    }
  }
  customElements.get('lx-tabs') || customElements.define('lx-tabs', LxTabs);

  /* ---------- Countdown ---------- */
  class LxCountdown extends HTMLElement {
    connectedCallback() {
      const raw = (this.dataset.end || '').trim();
      this.end = raw ? new Date(raw.replace(' ', 'T')).getTime() : NaN;
      if (Number.isNaN(this.end)) { this.hidden = true; return; }
      this.els = {
        d: this.querySelector('[data-d]'),
        h: this.querySelector('[data-h]'),
        m: this.querySelector('[data-m]'),
        s: this.querySelector('[data-s]'),
      };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      let t = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
      if (t === 0) {
        clearInterval(this.timer);
        if (this.dataset.hideExpired === 'true' && !window.Shopify?.designMode) this.closest('[data-lx-offer]')?.setAttribute('hidden', '');
      }
      const d = Math.floor(t / 86400); t -= d * 86400;
      const h = Math.floor(t / 3600); t -= h * 3600;
      const m = Math.floor(t / 60); const s = t - m * 60;
      const pad = (n) => String(n).padStart(2, '0');
      if (this.els.d) this.els.d.textContent = pad(d);
      if (this.els.h) this.els.h.textContent = pad(h);
      if (this.els.m) this.els.m.textContent = pad(m);
      if (this.els.s) this.els.s.textContent = pad(s);
    }
  }
  customElements.get('lx-countdown') || customElements.define('lx-countdown', LxCountdown);

  /* ---------- Shop the look hotspots ---------- */
  class LxLook extends HTMLElement {
    connectedCallback() {
      this.spots = [...this.querySelectorAll('.lx-hotspot')];
      this.items = [...this.querySelectorAll('.lx-look__item')];
      const activate = (i) => {
        this.spots.forEach((s, k) => s.classList.toggle('is-active', k === i));
        this.items.forEach((it, k) => it.classList.toggle('is-active', k === i));
      };
      this.spots.forEach((s, i) => {
        s.addEventListener('mouseenter', () => activate(i));
        s.addEventListener('focus', () => activate(i));
        s.addEventListener('click', () => {
          activate(i);
          const item = this.items[i];
          if (item && window.matchMedia('(max-width: 849px)').matches) item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
      });
      this.items.forEach((it, i) => it.addEventListener('mouseenter', () => activate(i)));
      if (this.spots.length) activate(0);
    }
  }
  customElements.get('lx-look') || customElements.define('lx-look', LxLook);

  /* ---------- Estimated delivery window ---------- */
  class LxDelivery extends HTMLElement {
    connectedCallback() {
      const min = parseInt(this.dataset.min, 10) || 3;
      const max = parseInt(this.dataset.max, 10) || 7;
      const addBusinessDays = (date, days) => {
        const d = new Date(date);
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          const wd = d.getDay();
          if (wd !== 0 && wd !== 6) added++;
        }
        return d;
      };
      const fmt = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { weekday: 'short', month: 'short', day: 'numeric' });
      const now = new Date();
      const out = this.querySelector('[data-range]');
      if (out) out.textContent = `${fmt.format(addBusinessDays(now, min))} – ${fmt.format(addBusinessDays(now, max))}`;
    }
  }
  customElements.get('lx-delivery') || customElements.define('lx-delivery', LxDelivery);

  /* ---------- Copy discount code ---------- */
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-lx-copy]');
    if (!btn) return;
    const label = btn.querySelector('[data-label]');
    const original = label?.textContent;
    try {
      await navigator.clipboard.writeText(btn.dataset.lxCopy);
      btn.classList.add('is-copied');
      if (label) label.textContent = 'Copied';
      setTimeout(() => { btn.classList.remove('is-copied'); if (label) label.textContent = original; }, 2000);
    } catch (_) { /* clipboard unavailable */ }
  });

  /* ---------- Quick add (single-variant products) ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-lx-quick-add]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('button');
    const label = btn.querySelector('[data-label]');
    const original = label.textContent;
    btn.disabled = true;
    label.textContent = 'Adding…';
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(form.id.value), quantity: 1 }] }),
      });
      if (!res.ok) throw new Error('add failed');
      const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
      btn.classList.add('is-added');
      label.textContent = 'Added to cart';
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: cart, sourceId: 'lunemi-quick-add', data: { itemCount: cart.item_count, source: 'lunemi-quick-add' } },
        })
      );
    } catch (err) {
      label.textContent = 'Try again';
    } finally {
      setTimeout(() => { btn.disabled = false; btn.classList.remove('is-added'); label.textContent = original; }, 2200);
    }
  });

  /* ---------- Reveal on scroll ---------- */
  const revealAll = (root = document) => {
    const els = root.querySelectorAll('[data-lx-reveal]:not(.is-visible)');
    if (!('IntersectionObserver' in window) || reduceMotion) { els.forEach((el) => el.classList.add('is-visible')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-visible'); io.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    els.forEach((el) => io.observe(el));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => revealAll());
  else revealAll();
  document.addEventListener('shopify:section:load', (e) => revealAll(e.target));

  /* ---------- Optional external checkout (set in Theme settings › Lunemi checkout) ---------- */
  const cfg = window.LunemiConfig || {};
  if (cfg.externalCheckoutUrl) {
    const getCookie = (name) => {
      const m = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
      return m ? m[2] : null;
    };
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('#checkout, #CartDrawer-Checkout, button[name="checkout"], input[name="checkout"], a[href$="/checkout"]');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      try {
        const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart?view=meta-data.json`, { headers: { Accept: 'application/json' } });
        const data = await res.json();
        const items = (data.meta_product_id_array || []).map((it) => `${it.id}:${it.quantity}`).join(';');
        // Same URL format as the previous store's checkout integration
        let url = `${cfg.externalCheckoutUrl}${cfg.externalCheckoutUrl.includes('?') ? '&' : '?'}products=${items}&cartId=${getCookie('cart') || ''}`;
        const affId = getCookie('affId');
        if (affId) url += `&affId=${affId}`;
        window.location.href = url;
      } catch (err) {
        console.error('[Lunemi] External checkout failed, falling back to Shopify checkout.', err);
        window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
      }
    }, true);
  }
})();

/* Collapse footer columns on small screens */
(() => {
  const collapse = () => {
    if (!window.matchMedia('(max-width: 849px)').matches) return;
    document.querySelectorAll('.lx-footer__col details[open]').forEach((d) => d.removeAttribute('open'));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', collapse);
  else collapse();
})();
